// Capture-map keys — how a schema field's `target` maps to where its value lives
// in the web capture UI.
//
// The capture layer (validation, conditional logic, calculations, default
// seeding) works over ONE flat map. Most fields bind to a `row_data` column, but
// a field can also target the row's own `name` / `description` columns — those
// are not part of `row_data`, so they get reserved keys in the same map:
//
//   { ...rowData, $name: <row name>, $description: <row description> }
//
// The host (EntityForm) splits the reserved keys back out when writing. The `$`
// prefix cannot collide with a real column: backend column keys are slugs
// (`[a-z0-9_]`).
//
// Kept in its own module so `captureValidation` and `conditions` can both use it
// without importing each other.

/** Reserved capture key for a field bound to the row's `name` column. */
export const NAME_CAPTURE_KEY = "$name";
/** Reserved capture key for a field bound to the row's `description` column. */
export const DESCRIPTION_CAPTURE_KEY = "$description";

/** The flat capture map: every `row_data` column plus the two reserved keys. */
export type CaptureValues = Record<string, unknown>;

/** `target` → the `row_data` key, or null for name/description/display targets. */
export function rowDataKeyOf(target: string | null | undefined): string | null {
  return typeof target === "string" && target.startsWith("row_data.") ? target.slice("row_data.".length) : null;
}

/**
 * `target` → the key its value lives under in the capture map: a `row_data`
 * column, `$name` / `$description` for the row's own columns, or null for a
 * display field (which captures nothing).
 */
export function captureKeyOf(target: string | null | undefined): string | null {
  if (target === "name") return NAME_CAPTURE_KEY;
  if (target === "description") return DESCRIPTION_CAPTURE_KEY;
  return rowDataKeyOf(target);
}

/** Split a capture map back into the row's own columns + `row_data`. */
export function splitCaptureValues(values: CaptureValues): { name?: string; description?: string; rowData: Record<string, unknown> } {
  const { [NAME_CAPTURE_KEY]: name, [DESCRIPTION_CAPTURE_KEY]: description, ...rowData } = values;
  return {
    ...(name === undefined ? {} : { name: String(name ?? "") }),
    ...(description === undefined ? {} : { description: String(description ?? "") }),
    rowData
  };
}

/** Build a capture map from the row's own columns + `row_data`. */
export function buildCaptureValues(rowData: Record<string, unknown>, name: string, description: string): CaptureValues {
  return { ...rowData, [NAME_CAPTURE_KEY]: name, [DESCRIPTION_CAPTURE_KEY]: description };
}
